import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { CalendarDays, TrendingUp, NotebookPen } from "lucide-react";

const MOODS = { content: "Content", joyeux: "Joyeux", calme: "Calme", fatigue: "Fatigué", agite: "Agité", triste: "Triste" };
const day = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

function Panel({ Icon, title, sub, children, testId }) {
  return (
    <div className="rounded-3xl bg-white border border-border/60 p-6" data-testid={testId}>
      <div className="flex items-center gap-3 mb-1"><Icon className="w-5 h-5 text-primary" /><h3 className="font-serif-display text-2xl">{title}</h3></div>
      <p className="text-gray-500 mb-4">{sub}</p>
      {children}
    </div>
  );
}

function Week({ items, name }) {
  return (
    <Panel Icon={CalendarDays} title="Sa semaine" sub={`Les activités faites par ${name} ces 7 derniers jours.`} testId="family-week">
      {items.length === 0 ? <p className="text-gray-400 py-4 text-center">Pas encore d'activité cette semaine.</p> : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map((a, i) => (
            <li key={i} className="flex items-center justify-between gap-3 rounded-2xl bg-accent/40 px-4 py-3" data-testid={`family-week-item-${i}`}>
              <span className="font-semibold text-gray-700">{a.name}</span>
              <span className="text-sm text-gray-500 text-right first-letter:uppercase">{day(a.date)} · {a.minutes} min</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Progress({ points, name }) {
  const has = points.some((p) => p.level != null);
  return (
    <Panel Icon={TrendingUp} title="Son évolution" sub={`L'élan de ${name} semaine après semaine.`} testId="family-progress">
      {!has ? <p className="text-gray-400 py-4 text-center">La courbe apparaîtra après les premières activités.</p> : (
        <div className="h-48" data-testid="family-progress-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="famGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2E8EC2" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2E8EC2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="week" tickLine={false} axisLine={false} interval={0} padding={{ left: 12, right: 12 }} tick={{ fontSize: 12, fill: "#8a8f94" }} />
              <YAxis hide domain={[0, 100]} />
              <Tooltip formatter={(v, n, p) => [`${p.payload.count} activité(s)`, "Semaine"]} labelFormatter={(l) => `Semaine jusqu'au ${l}`} />
              <Area type="monotone" dataKey="level" stroke="#2E8EC2" strokeWidth={3} fill="url(#famGrad)" connectNulls dot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

function Notes({ notes }) {
  return (
    <Panel Icon={NotebookPen} title="Mots de l'équipe soignante" sub="Les observations que l'équipe a choisi de partager avec vous." testId="family-shared-notes">
      {notes.length === 0 ? <p className="text-gray-400 py-4 text-center">Aucune note partagée pour le moment.</p> : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="rounded-2xl bg-warm/10 border border-warm/25 p-4" data-testid={`family-note-${n.id}`}>
              <div className="flex flex-wrap justify-between gap-2 mb-1">
                <span className="font-semibold text-gray-700">{n.exercise_name || "Atelier"}</span>
                <span className="text-sm text-gray-500 first-letter:uppercase">{day(n.created_at)}</span>
              </div>
              {n.mood && <p className="text-sm text-gray-600">Humeur : <b>{MOODS[n.mood] || n.mood}</b></p>}
              {n.observation && <p className="text-gray-700 mt-2 leading-relaxed">{n.observation}</p>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export default function FamilyInfo({ data }) {
  const name = data.patient.name;
  return (
    <div className="space-y-6 mb-8" data-testid="family-info">
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Week items={data.week_activities || []} name={name} />
        <Progress points={data.progress || []} name={name} />
      </div>
      <Notes notes={data.shared_notes || []} />
    </div>
  );
}
